import { assistantService, AssistantMessage } from './assistantService';
import { chatHistoryService, ChatMessage } from './chatHistoryService';
import { tokenCounterService } from './tokenCounterService';
import authProvider from '../authProvider';

/**
 * Service for managing context continuation with automatic summarization
 */
class ContextContinuationService {
  /**
   * Check if summarization is needed and perform it if necessary
   */
  async checkAndSummarize(userId: string): Promise<void> {
    const history = await chatHistoryService.loadHistory(userId);
    if (!history || history.messages.length === 0) {
      return;
    }

    // Convert ChatMessage[] to AssistantMessage[]
    const messages: AssistantMessage[] = history.messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));

    // Count tokens
    const tokenCount = tokenCounterService.countTokens(messages);

    // Check if threshold reached
    if (tokenCounterService.shouldSummarize(tokenCount)) {
      console.log(
        `[ContextContinuation] Token count ${tokenCount} exceeds threshold, summarizing...`
      );

      await this.summarizeHistory(userId, messages);
    }
  }

  /**
   * Summarize conversation history
   */
  private async summarizeHistory(
    userId: string,
    messages: AssistantMessage[]
  ): Promise<void> {
    try {
      // Call API to summarize
      const summaryResponse = await assistantService.summarize({
        messages,
        currentUserId: userId,
      });

      // Replace history with summary
      await chatHistoryService.replaceHistoryWithSummary(
        userId,
        summaryResponse.summary
      );

      console.log(
        `[ContextContinuation] History summarized: ${summaryResponse.originalTokenCount} → ${summaryResponse.tokenCount} tokens`
      );
    } catch (error) {
      console.error('[ContextContinuation] Error summarizing history:', error);
      // Don't throw - allow conversation to continue even if summarization fails
    }
  }

  /**
   * Add message and check for summarization
   */
  async addMessageAndCheck(
    userId: string,
    message: ChatMessage
  ): Promise<void> {
    // Save message
    await chatHistoryService.saveMessage(message);

    // Check if summarization is needed
    await this.checkAndSummarize(userId);
  }

  /**
   * Get conversation context for API call
   * Returns recent messages + summary if available
   */
  async getConversationContext(userId: string): Promise<AssistantMessage[]> {
    const history = await chatHistoryService.loadHistory(userId);
    if (!history) return [];

    const messages: AssistantMessage[] = [];

    // Add summary if available
    if (history.summary) {
      messages.push({
        role: 'system',
        content: `Previous conversation summary: ${history.summary}`,
      });
    }

    // Add recent messages (last 10 to keep context manageable)
    const recentMessages = history.messages.slice(-10);
    messages.push(
      ...recentMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }))
    );

    return messages;
  }
}

export const contextContinuationService = new ContextContinuationService();
